/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Keeps a priced quote in step with the cart.
 *
 * The same job the coupon's re-pricing effect does in EventEntryModal, and
 * deliberately NOT folded into it: that effect only runs while a code is
 * applied, and tax applies whether or not anybody typed a code. Kept out of
 * the modal because that file is already 1,700 lines, and this is a
 * self-contained unit with one input and one output.
 *
 * KEYED ON THE CART'S CONTENT, NOT ITS IDENTITY. `items` is rebuilt on every
 * render, so an effect depending on the array would re-fetch on every
 * keystroke in an unrelated field. The serialised content is the real
 * dependency: it changes when a seat is added and at no other time.
 *
 * DEBOUNCED, because a runner going from one seat to five presses the stepper
 * four times in about a second, and each press would otherwise be a round trip
 * whose answer is already stale when it lands.
 *
 * NEVER THROWS AND NEVER BLOCKS. A failed quote resolves to null, which the
 * caller reads as "the tax is not known yet" and falls back to the pre-tax
 * figures. Tax is additive to a flow that already worked; it must not be able
 * to stop a runner paying.
 */

import { useEffect, useRef, useState } from 'react';
import { fetchCheckoutQuote } from './checkoutQuote';

export default function useCheckoutQuote({
  enabled, items, couponCode, applicationId, debounceMs = 300,
} = {}) {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  // What the quote on screen was priced against. Compared rather than trusted
  // so a late answer to a question nobody is asking any more is dropped —
  // the same guard the modal's runIdRef provides for the write path.
  const askedForRef = useRef(null);

  const cart = Array.isArray(items) ? items : [];
  const key = enabled && cart.length > 0
    ? JSON.stringify({ items: cart, couponCode: couponCode || null })
    : null;

  useEffect(() => {
    if (!key) {
      // An emptied or free cart has nothing to price. Cleared rather than left
      // showing the last figure, which would otherwise sit under a cart that
      // no longer earns it.
      askedForRef.current = null;
      setQuote(null);
      setBusy(false);
      return undefined;
    }

    let cancelled = false;
    setBusy(true);

    const timer = setTimeout(async () => {
      const answer = await fetchCheckoutQuote({
        applicationId,
        items: cart,
        couponCode: couponCode || undefined,
      });
      if (cancelled) return;
      askedForRef.current = key;
      // Null on failure, which is exactly what is wanted: the summary then
      // falls back to the pre-tax figures rather than showing a stale total
      // belonging to a different cart.
      setQuote(answer);
      setBusy(false);
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `cart` is intentionally absent — `key` IS its content, and depending on
    // the array as well would defeat the point of keying on the content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, applicationId, debounceMs]);

  return {
    quote,
    // True only while an answer is actually outstanding for the CURRENT cart.
    // Used to dim the figures rather than to disable anything: a runner must
    // never be held at the Pay button waiting on a tax lookup.
    quoteBusy: busy && askedForRef.current !== key,
    // Whether the figures on screen are the server's or the page's own
    // fallback. The summary needs to know, because it must not print a
    // "Total payable" with tax silently missing from it.
    quoted: Boolean(quote) && askedForRef.current === key,
  };
}
